import React, {useCallback, useState} from 'react'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import {
  validateMemberPasswordsInput,
  ValidationPasswordsInput,
} from '../../../Utils/Validations/ValidateMembersPasswordInput'
import MemberRepo from '../../../Repo/MemberRepo'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {MBMemberError} from '../../../Common/MBMemberError'
import {animateScroll} from 'react-scroll'
import {Logout} from '../../../../Common/Components/Logout'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {membersMsalInstance} from '../../../Auth/members-msa'
import {PasswordInputContent} from '../../Components/InputContentBlocks/PasswordInputContent'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {Spacer} from '../../../../Common/Components/Spacer'
import {useAsync} from 'react-use'
import {useMsal} from '@azure/msal-react'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'

type MembersEditPasswordPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

type State = {
  password: string
}

type ErrorState = {
  isInputError: boolean
  passwordErrors: string[]
}

export const MembersEditPasswordPage: React.VFC<MembersEditPasswordPageProps> = (props) => {
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [processing, setProcessing] = useState<boolean>(false)
  const [state, setState] = useState<State>({
    password: '',
  })
  const [errorState, setErrorState] = useState<ErrorState>({
    isInputError: false,
    passwordErrors: [],
  })
  const [isEditPasswordSucceeded, setIsEditPasswordSucceeded] = useState<boolean>(false)

  const [showPasswordChecked, setShowPasswordChecked] = useState(false)

  const throwMBMemberError = useMBMemberError()

  const {inProgress} = useMsal()

  const handlePasswordInputChange = useCallback((password: string) => {
    setState({password: password})
  }, [])

  const goToAccountInformationPage = useCallback((): void => {
    props.navigator.goToAccountInformationPage()
  }, [props.navigator])

  const editPassword = useCallback(
    async (): Promise<void> => {
      setShowPasswordChecked(false)

      setProcessing(true)

      const inputForValidation: ValidationPasswordsInput = {
        ...state,
        password: state.password,
      }

      setState((prevState) => ({
        ...prevState,
        ...inputForValidation,
      }))

      const validationResult = validateMemberPasswordsInput(inputForValidation)

      if (validationResult.hasError) {
        setProcessing(false)
        setErrorState({
          isInputError: true,
          passwordErrors: validationResult.errors.password,
        })
        animateScroll.scrollToTop()
        return

      }

      try {
        const result = await props.memberRepo.putPassword(inputForValidation.password)
        switch (result.status) {
          case CommonResponseStatus.Ok:
            break
          case CommonResponseStatus.BadRequest:
            setProcessing(false)
            setErrorMessages(result.errorMessageList)
            animateScroll.scrollToTop()
            break
          case CommonResponseStatus.InternalServerError:
            // noinspection ExceptionCaughtLocallyJS
            throw new MBMemberError('ServerError')
          default:
            // noinspection ExceptionCaughtLocallyJS
            throw new MBMemberError('UnknownError')
        }
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'SystemError'
        throwMBMemberError(errorMessage)
      }

      setIsEditPasswordSucceeded(true)
    }
    , [props.memberRepo, state])

  const result = useAsync(async () => {
    return await props.memberRepo.getSelf()
  })

  if (result.loading) {
    return null
  }
  if (result.error) {
    throw new MBMemberError(result.error.message)
  }
  if (!result.value) {
    throw new MBMemberError('UnknownError')
  }
  if (!result.value.data.member) {
    throw new MBMemberError('UnknownError')
  }

  memberResponseStatusCheck(result.value.status)

  const {mail} = result.value.data.member
  const {password} = state
  const {isInputError, passwordErrors} = errorState

  return (
    <MembersPageTemplate>
      <PageTitle>パスワード変更</PageTitle>

      <InputErrorSummary display={isInputError}/>

      <Spacer mb={'16px'}>
        {/*iPhoneのパスワード更新のため*/}
        <input type={'hidden'} id={'email'} value={mail}/>
        <PIMStack alignItems={'center'}>
          <PasswordInputContent
            title={'パスワードの入力'}
            topMessage={'新しいパスワードを設定してください。'}
            showRequiredBadge={false}
            password={password}
            onChange={handlePasswordInputChange}
            passwordErrors={passwordErrors}
            defaultShowPasswordChecked={showPasswordChecked}
            onCheckboxChange={(checked: boolean): void => {
              setShowPasswordChecked(checked)
            }}
          />
        </PIMStack>
      </Spacer>

      <MessageForAction>
        上記の内容に間違いがなければ<br/>『パスワードを変更する』ボタンを押してください。
      </MessageForAction>

      <ErrorDisplay errors={errorMessages}/>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'change-the-password-button'}
          disabled={processing}
          onClick={(): void => {
            editPassword()
          }}
        >
          パスワードを変更する
        </PIMSubmitButton>
        <PIMSecondaryButton onClick={goToAccountInformationPage}>
          登録情報に戻る
        </PIMSecondaryButton>
      </PIMPageActions>

      {isEditPasswordSucceeded && <Logout
        msalInstance={membersMsalInstance}
        postLogoutRedirectUri="/account/information/password/update_complete"
        throwMBError={throwMBMemberError}
        navigate={(): void => {
          props.navigator.goToEditPasswordCompletePage()
        }}
        inProgress={inProgress}/>}
    </MembersPageTemplate>
  )
}
